import React, { useState, useEffect } from 'react';
import { AlertTriangle, History, Save, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface SystemPrompt {
  id: string;
  prompt: string;
  created_at: string;
  is_active: boolean;
}

const SystemPromptEditor: React.FC = () => {
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('system_prompts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        setPrompts(data);
        const activePrompt = data.find(p => p.is_active);
        if (activePrompt) {
          setCurrentPrompt(activePrompt.prompt);
        }
      }
    } catch (err) {
      console.error('Error loading prompts:', err);
      setError('Failed to load system prompts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('system_prompts')
        .insert({
          prompt: currentPrompt,
          is_active: true,
          created_by: user.id
        });

      if (error) throw error;

      toast.success('System prompt updated successfully');
      await loadPrompts();
    } catch (err) {
      console.error('Error saving prompt:', err);
      setError('Failed to save system prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestorePrompt = async (prompt: SystemPrompt) => {
    try {
      setIsSaving(true);
      setError(null);

      const { error } = await supabase
        .from('system_prompts')
        .update({ is_active: true })
        .eq('id', prompt.id);

      if (error) throw error;

      setCurrentPrompt(prompt.prompt);
      toast.success('Previous prompt restored successfully');
      await loadPrompts();
    } catch (err) {
      console.error('Error restoring prompt:', err);
      setError('Failed to restore previous prompt');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse text-gray-600">
        Loading system prompts...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          System Prompt Editor
        </h2>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-lg text-red-700 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        <div className="space-y-4">
          <textarea
            value={currentPrompt}
            onChange={(e) => setCurrentPrompt(e.target.value)}
            className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            placeholder="Enter system prompt..."
          />

          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <History className="h-5 w-5 mr-2" />
              {showHistory ? 'Hide History' : 'Show History'}
              {showHistory ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              )}
            </button>

            <button
              onClick={handleSave}
              disabled={isSaving || !currentPrompt.trim()}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {showHistory && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Prompt History</h3>
          {prompts.map((prompt) => (
            <div
              key={prompt.id}
              className="bg-white rounded-lg shadow-sm p-6 space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">
                    {new Date(prompt.created_at).toLocaleString()}
                  </span>
                  {prompt.is_active && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  )}
                </div>
                {!prompt.is_active && (
                  <button
                    onClick={() => handleRestorePrompt(prompt)}
                    disabled={isSaving}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    Restore this version
                  </button>
                )}
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{prompt.prompt}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SystemPromptEditor;